import React, { useState } from 'react';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { ArrowRight, TrendingUp } from 'lucide-react';
import { portfolioProjects, personalBrandSuccess } from '../data/mock';

const Portfolio = () => {
  const [activeTab, setActiveTab] = useState('brands');

  return (
    <section className="py-16 px-6" id="portfolio">
      <div className="max-w-6xl mx-auto">
        {/* Section Header */}
        <div className="text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Portfolio & Results
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Real results for real brands. See how strategic content creation and brand positioning drives measurable growth.
          </p>
        </div>

        {/* Tab Navigation */}
        <div className="flex justify-center mb-12">
          <div className="bg-white rounded-full p-1 shadow-md border">
            <button
              onClick={() => setActiveTab('brands')}
              className={`px-6 py-3 rounded-full font-medium transition-all duration-300 ${
                activeTab === 'brands' 
                  ? 'bg-gray-900 text-white shadow-lg' 
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Brand Projects
            </button>
            <button
              onClick={() => setActiveTab('personal')}
              className={`px-6 py-3 rounded-full font-medium transition-all duration-300 ${
                activeTab === 'personal' 
                  ? 'bg-gray-900 text-white shadow-lg' 
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Personal Brands
            </button>
          </div>
        </div>

        {/* Brand Projects */}
        {activeTab === 'brands' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 justify-items-center">
            {portfolioProjects.map((project) => (
              <Card key={project.id} className="group overflow-hidden border-0 shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105">
                <div className="relative">
                  <img 
                    src={project.image} 
                    alt={project.title}
                    className="w-full h-48 object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute top-4 left-4">
                    <Badge className="bg-white/90 text-gray-900 font-medium">
                      {project.category}
                    </Badge>
                  </div>
                  <div className="absolute top-4 right-4">
                    <div className="bg-gradient-to-r from-pink-500 to-purple-500 text-white px-3 py-1 rounded-full text-sm font-bold">
                      {project.highlight}
                    </div>
                  </div>
                </div>
                
                <CardContent className="p-6">
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">
                    {project.title}
                  </h3>
                  
                  <div className="space-y-4">
                    <div>
                      <h4 className="font-medium text-gray-700 mb-2">Challenge:</h4>
                      <p className="text-sm text-gray-600 leading-relaxed">
                        {project.challenge}
                      </p>
                    </div>
                    
                    <div>
                      <h4 className="font-medium text-gray-700 mb-2">Key Results:</h4>
                      <div className="space-y-1">
                        {project.results.slice(0, 2).map((result, index) => (
                          <div key={index} className="flex items-center gap-2">
                            <TrendingUp className="h-4 w-4 text-green-600" />
                            <span className="text-sm text-gray-700">{result}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                  
                  <Button 
                    variant="ghost" 
                    className="w-full mt-4 text-gray-600 hover:text-gray-900 hover:bg-gray-50"
                  >
                    {project.caseStudyLink ? (
                      <a 
                        href={project.caseStudyLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center justify-center w-full"
                      >
                        View Case Study
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </a>
                    ) : (
                      <>
                        View Case Study
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </>
                    )}
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Personal Brand Success Stories */}
        {activeTab === 'personal' && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {personalBrandSuccess.map((brand) => (
              <Card key={brand.id} className="group text-center border-0 shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105">
                <CardContent className="p-8">
                  <div className="relative mb-6">
                    <img 
                      src={brand.image} 
                      alt={brand.name}
                      className="w-20 h-20 rounded-full mx-auto object-cover group-hover:scale-110 transition-transform duration-300"
                    />
                  </div>
                  
                  <h3 className="text-lg font-semibold text-gray-900 mb-1">
                    {brand.name}
                  </h3>
                  <p className="text-gray-600 text-sm mb-4">{brand.role}</p>
                  
                  <div className="space-y-3 text-left">
                    <div>
                      <h4 className="font-medium text-gray-700 text-sm mb-1">Challenge:</h4>
                      <p className="text-xs text-gray-600 leading-relaxed">
                        {brand.challenge}
                      </p>
                    </div>
                    
                    <div 
                      className="p-3 rounded-lg"
                      style={{
                        background: `linear-gradient(135deg, #ffe6fe 0%, #fdecff 100%)`
                      }}
                    >
                      <h4 className="font-medium text-gray-800 text-sm mb-1">Result:</h4>
                      <p className="text-sm font-semibold text-gray-900">
                        {brand.result}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Bottom CTA */}
        <div className="text-center mt-16">
          <h3 className="text-2xl font-semibold text-gray-900 mb-4">
            Ready to See Similar Results?
          </h3>
          <p className="text-gray-600 mb-8 max-w-2xl mx-auto">
            Every successful project starts with understanding your unique goals. Let's create a strategy that delivers measurable growth for your brand.
          </p>
          <Button 
            size="lg"
            className="bg-gray-900 hover:bg-gray-800 text-white px-8 py-3 rounded-full transition-all duration-300 hover:scale-105 shadow-lg"
          >
            Discuss Your Project
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </div>
      </div>
    </section>
  );
};

export default Portfolio;